import { useEffect, useMemo, useState } from "react";
import { Avatar } from "@/components/Avatar";
import { useSearchParams } from "react-router-dom";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import { api, errMsg } from "@/lib/api";
import { Spinner } from "@/components/kit";
import { SupporterNoteForm } from "@/components/SupporterNoteForm";
import { SupporterSentNotes } from "@/components/SupporterSentNotes";
import { SupporterStreak } from "@/components/SupporterStreak";
import { SupporterStreakHug } from "@/components/SupporterStreakHug";
import { SupporterForecastNote } from "@/components/SupporterForecastNote";
import { SupporterWelcome } from "@/components/SupporterWelcome";
import { SupporterHeartAlerts } from "@/components/SupporterHeartAlerts";
import { SupporterBadgeCheers } from "@/components/SupporterBadgeCheers";
import { SupporterLinkStatus } from "@/components/SupporterLinkStatus";
import { SupporterThanksWall } from "@/components/SupporterThanksWall";
import { SupporterGratitudeForm } from "@/components/SupporterGratitudeForm";
import { SupporterOpenWhenForm } from "@/components/SupporterOpenWhenForm";
import { SupporterHelpCard } from "@/components/playbook/HelpCardView";

const Stat = ({ label, value }) => (
  <div className="mm-soft p-4 text-center"><div className="font-display text-2xl font-bold text-mm-primary">{value}</div><div className="text-xs text-mm-muted mt-1">{label}</div></div>
);

const MOOD_DOMAIN = [0, 10];
const BAR_RADIUS = [8, 8, 0, 0];

export default function SupporterView() {
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    api.get(`/supporters/view/${params.get("token")}`).then((r) => setData(r.data)).catch((e) => { setExpired(e.response?.status === 410); setError(errMsg(e, "Invalid link")); });
  }, [params]);
  const summaryStats = useMemo(
    () => Object.entries(data?.weekly_summary || {}).filter(([, v]) => typeof v !== "object"),
    [data],
  );

  if (expired) return <div className="min-h-screen flex items-center justify-center p-5"><div className="max-w-lg w-full" data-testid="supporter-view-expired"><SupporterLinkStatus token={params.get("token")} /></div></div>;
  if (error) return <div className="min-h-screen flex items-center justify-center p-5"><div className="mm-card p-10 text-center" data-testid="supporter-view-error"><div className="text-4xl">🔒</div><p className="mt-3">{error}</p></div></div>;
  if (!data) return <Spinner className="min-h-screen items-center" />;
  const mt = data.mood_trends, st = data.streaks, ws = data.weekly_summary;

  return (
    <div className="min-h-screen mm-gradient-soft px-5 py-12">
      <div className="max-w-2xl mx-auto space-y-5 mm-stagger" data-testid="supporter-view">
        <div>
          <span className="mm-chip !cursor-default !text-xs">🔒 Read-only supporter view</span>
          <div className="flex items-center gap-4 mt-4">
            <Avatar url={data.user_photo} name={data.user_name} size={56} testId="supporter-view-avatar" />
            <h1 className="text-4xl font-bold">{data.user_name}'s wellness</h1>
          </div>
          <p className="text-mm-muted mt-1">Shared with you privately. Journal entries are never shared.</p>
        </div>
        <SupporterWelcome token={params.get("token")} name={data.user_name?.split(" ")[0] || "your friend"} welcomed={data.welcomed} />
        <SupporterLinkStatus token={params.get("token")} />
        {mt && (
          <div className="mm-card p-6">
            <h2 className="font-semibold">Mood this week · avg {mt.week_average} · trend {mt.trend}</h2>
            <div className="h-56 mt-4">
              <ResponsiveContainer><BarChart data={mt.data}><XAxis dataKey="day" /><YAxis domain={MOOD_DOMAIN} /><Tooltip /><Bar dataKey="mood" fill="var(--mm-primary)" radius={BAR_RADIUS} /></BarChart></ResponsiveContainer>
            </div>
          </div>
        )}
        {st && <div className="mm-card p-6 grid grid-cols-3 gap-3"><Stat label="Current streak" value={st.current ?? st.current_streak} /><Stat label="Longest streak" value={st.longest ?? st.longest_streak} /><Stat label="Total check-ins" value={st.total_checkins} /></div>}
        {ws && (
          <div className="mm-card p-6">
            <h2 className="font-semibold mb-4">Weekly summary</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {summaryStats.map(([k, v]) => <Stat key={k} label={k.replace(/_/g, " ")} value={v} />)}
            </div>
          </div>
        )}
        <SupporterForecastNote token={params.get("token")} name={data.user_name?.split(" ")[0] || "them"} />
        <SupporterStreakHug token={params.get("token")} name={data.user_name?.split(" ")[0] || "them"} />
        <SupporterBadgeCheers token={params.get("token")} name={data.user_name?.split(" ")[0] || "them"} />
        <SupporterStreak token={params.get("token")} name={data.user_name?.split(" ")[0] || "them"} />
        <SupporterNoteForm token={params.get("token")} name={data.user_name?.split(" ")[0] || "them"} />
        <SupporterHelpCard token={params.get("token")} name={data.user_name?.split(" ")[0] || "them"} />
        {data.open_when_letters && <SupporterOpenWhenForm token={params.get("token")} name={data.user_name?.split(" ")[0] || "them"} />}
        {data.gratitude_notes && <SupporterGratitudeForm token={params.get("token")} name={data.user_name?.split(" ")[0] || "them"} />}
        <SupporterHeartAlerts token={params.get("token")} name={data.user_name?.split(" ")[0] || "them"} />
        <SupporterThanksWall token={params.get("token")} name={data.user_name?.split(" ")[0] || "them"} />
        <SupporterSentNotes token={params.get("token")} name={data.user_name?.split(" ")[0] || "them"} />
      </div>
    </div>
  );
}
